import React, { useMemo, useRef, useEffect } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { File, Check } from "lucide-react";
import { useWorkspaceStore } from "@/stores/workspace";
import { FileNode } from "@/pages/workspace/types";
import { useUiStore } from "@/stores/ui";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export const WorkspaceCommandPalette = () => {
  const {
    fileTree,
    expandedFolders,
    setExpandedFolders,
    openInPreview,
    setUserSelectedFiles,
    userSelectedFiles,
  } = useWorkspaceStore((state) => ({
    fileTree: state.fileTree,
    expandedFolders: state.expandedFolders,
    setExpandedFolders: state.setExpandedFolders,
    openInPreview: state.openInPreview,
    setUserSelectedFiles: state.setUserSelectedFiles,
    userSelectedFiles: state.userSelectedFiles,
  }));
  const { isCommandPaletteOpen: isOpen, setIsCommandPaletteOpen } =
    useUiStore();
  const listRef = useRef<HTMLDivElement>(null);

  const shiftPressedOnEnter = useRef(false);
  const wasClicked = useRef(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        shiftPressedOnEnter.current = e.shiftKey;
      }
    };
    if (isOpen) {
      // Use capture to get it before cmdk
      document.addEventListener("keydown", handleKeyDown, true);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen]);

  const allFiles = useMemo(() => {
    const files: { path: string; name: string }[] = [];
    const traverse = (nodes: FileNode[]) => {
      for (const node of nodes) {
        if (node.type === "file" && node.tokenizable) {
          files.push({ path: node.path, name: node.name });
        }
        if (node.children) traverse(node.children);
      }
    };
    if (fileTree) {
      traverse(fileTree);
    }
    return files;
  }, [fileTree]);

  const handleFileSelect = (filePath: string) => {
    const addToContext = !wasClicked.current && shiftPressedOnEnter.current;

    const segments = filePath.split("/");
    if (segments.length > 1) {
      const newExpandedFolders = new Set(expandedFolders);
      for (let i = 1; i < segments.length; i++) {
        newExpandedFolders.add(segments.slice(0, i).join("/"));
      }
      setExpandedFolders(() => newExpandedFolders);
    }

    if (addToContext) {
      setUserSelectedFiles((prev) => new Set(prev).add(filePath));
      toast.success(`${filePath} added to context.`);
    }

    openInPreview(filePath);
    setIsCommandPaletteOpen(false);
  };

  const handleFileToggle = (filePath: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setUserSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(filePath)) {
        newSet.delete(filePath);
      } else {
        newSet.add(filePath);
        toast.success(`${filePath} added to context.`);
      }
      return newSet;
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsCommandPaletteOpen}>
      <DialogContent
        overlayClassName="bg-transparent"
        className="p-0 shadow-lg top-[20%] border-0 rounded-lg [&>button]:hidden translate-y-0 data-[state=open]:slide-in-from-top-[18%]"
      >
        <Command
          
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          <CommandInput placeholder="Search files by name (append : to go to line or @ to go to symbol)"  onValueChange={() => {
            if (listRef.current) {
              listRef.current.scrollTop = 0;
            }
          }}/>
          <CommandList ref={listRef}>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup heading="Files">
              {allFiles.map((file) => (
                <CommandItem
                  key={file.path}
                  onSelect={() => handleFileSelect(file.path)}
                  onMouseDownCapture={() => {
                    wasClicked.current = true;
                  }}
                >
                  <button
                    onClick={(e) => handleFileToggle(file.path, e)}
                    className={`mr-2 h-4 w-4 flex-shrink-0 rounded border transition-colors ${
                      userSelectedFiles.has(file.path)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-muted-foreground/30 hover:border-muted-foreground"
                    }`}
                  >
                    {userSelectedFiles.has(file.path) && <Check className="h-3 w-3" />}
                  </button>
                  <File className="mr-2 h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{file.path}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
